import * as React from 'react';
import { observer } from 'mobx-react-lite';
import { useStores } from '../../stores/Hooks';
import { FileLibrary } from '../../stores/libraries/FileLibrary';
import { FileDropZoneUploadingItemProgress } from './FileDropZoneUploadingItemProgress';
import { FileStateEnum } from '../../models/FileStateEnum';
import { StatementStatusEnum } from '../../models';
import { DeleteFormCallbackEnum, ModalTypeEnum } from '../../stores/libraries/ModalLibrary';

interface IFileDropZoneUploadingItem
{
	/** Идентификатор добавленного файла */
	FileId: string,

	/** Идентификатор типа файлов, который привязан к текущему списку загружаемых файлов */
	FileTypeId: string
}

export const FileDropZoneUploadingItem: React.FC<IFileDropZoneUploadingItem> = observer(
	(
		{ FileId, FileTypeId }: IFileDropZoneUploadingItem
	) =>
	{
		const { uiState, dataState } = useStores();
		const store = uiState.files;

		let fs: FileLibrary = store.findFileInList(FileTypeId, FileId);

		function shortFileName(name: string)
		{
			//"ON_NSCHFDOPPR_2BM-...85-967401000-2018.xml"  18 21
			if (name.length <= 39) return name;
			return name.substring(0, 18) + "..." + name.substring(name.length - 21, name.length);
		}

		function fileDelete(e: React.SyntheticEvent)
		{
			// если форма была уже создана
			if (uiState.StatementStatus !== StatementStatusEnum.NotCreated)
			{
				uiState.modal.SetChangeFormAction(DeleteFormCallbackEnum.DeletingFile);
				uiState.modal.SetDeletingFile(fs);
				uiState.modal.SetDeletingFileType(FileTypeId);
				uiState.modal.setShowModal(true, ModalTypeEnum.DeleteFormRequired, "Данные изменились", "Вы уверены, что хотите изменить данные?<br />Сформированное ранее заявление будет удалено из раздела “Заявление оператора ЭДО”.");
				return;
			}

			// Удаляем файл
			store.DeleteFileStore(FileTypeId, fs);
		}

		function dataStatus()
		{
			switch (fs.FileState)
			{
				case FileStateEnum.Started: return "in-progress";
				case FileStateEnum.Faulted: return "error";
				case FileStateEnum.Succeed: return "complete";
				case FileStateEnum.Waiting: return "waiting";
			}
		}

		function fileName()
		{
			if (uiState.IsDisabled) return fs.FileName;
			switch (fs.FileState)
			{
				case FileStateEnum.Started:
				case FileStateEnum.Waiting:
				case FileStateEnum.Faulted: return fs.FileName;
				case FileStateEnum.Succeed: return (<a href={dataState.GetFileLink(fs.FileTypeId, fs.FileId)}>{shortFileName(fs.FileName)}</a>);
			}
		}

		return (
			<div className="file-card d-flex" data-status={dataStatus()}>
				<div className="file-card__info d-flex align-items-center">
					<div className="file-card__data">
						<div className="file-card__filename mb-1">{fileName()}</div>
						<div className="file-card__status">
							<div className="in-progress">
								<div className="d-flex align-items-center">
									<div className="in-progress__size f-14">{store.shortFileSize(fs.FileSize)}</div>
									<FileDropZoneUploadingItemProgress FileId={FileId} FileTypeId={FileTypeId} />
								</div>
							</div>
							<div className="error">
								<p className="mb-0 f-14 text-danger">{fs.FileErrorMessage}</p>
							</div>
							<div className="complete">
								<div className="d-flex align-items-center flex-wrap">
									<span>Загружено</span>
									<span className="elipse-separator mx-2"></span>
									<span>{store.shortFileSize(fs.FileSize)}</span>
									<span className="elipse-separator mx-2"></span>
									<div className="d-flex align-items-center">
										<div className="input-text-block mr-2">
											<label className={"p-0 bg-white" + (fs.PageCount>0 ? "":" invalid")}>
												<input
													className="form-text short-input"
													type="text"
													pattern="^\d{1,3}$"
													maxLength={3}
													min={1}
													max={999}
													disabled={uiState.IsDisabled}
													value={fs.PageCount}
													onChange={(event: React.SyntheticEvent) => fs.setPageCount(Number((event.target as HTMLInputElement).value))}
												/>
											</label>
										</div>
										<span>cтраниц&nbsp;<span className="text-danger">*</span></span>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
				<div className="file-card__action ml-auto d-flex align-items-center">
					{fs.FileState !== FileStateEnum.Waiting &&
						<div className="file-card__close">
						{!uiState.IsDisabled &&
							<button className="btn btn-icon icon-delete" onClick={fileDelete}>
								<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
									<path d="M15.8334 5.34167L14.6584 4.16667L10.0001 8.82501L5.34175 4.16667L4.16675 5.34167L8.82508 10L4.16675 14.6583L5.34175 15.8333L10.0001 11.175L14.6584 15.8333L15.8334 14.6583L11.1751 10L15.8334 5.34167Z" />
								</svg>
							</button>
						}
						</div>
					}
					{fs.FileState === FileStateEnum.Waiting &&
						<div className="waiting d-flex align-items-center justify-content-center">
							<svg className="loader" width="76" height="76" viewBox="0 0 76 76" fill="none" xmlns="http://www.w3.org/2000/svg">
								<path d="M74.5059 38.5498C74.5059 58.77 58.1704 75.1055 37.9502 75.1055C17.73 75.1055 1.39453 58.77 1.39453 38.5498C1.39453 18.3297 17.8297 1.99414 38.0498 1.99414C58.1704 1.99414 74.5059 18.3297 74.5059 38.5498ZM6.57408 38.5498C6.57408 55.8814 20.6186 69.926 37.9502 69.926C55.2818 69.926 69.3264 55.8814 69.3264 38.5498C69.3264 21.2183 55.3814 7.07409 38.0498 7.07409C20.7183 7.07409 6.57408 21.2183 6.57408 38.5498Z" fill="#CCDDFF"></path>
								<path d="M72.0157 38.5498C73.4102 38.5498 74.6055 37.3545 74.5059 35.96C74.2071 32.0753 73.3106 28.1907 71.8165 24.5052C70.0236 20.1225 67.3342 16.0387 63.9476 12.652C60.5609 9.26539 56.5767 6.57601 52.0944 4.78309C48.4089 3.28898 44.6239 2.39252 40.6396 2.0937C39.2451 1.99409 38.0498 3.18938 38.0498 4.58387C38.0498 5.97837 39.2451 7.17365 40.6396 7.27326C43.827 7.47247 47.0144 8.26932 50.0026 9.4646C53.7877 11.0583 57.2739 13.3493 60.1625 16.2379C63.0511 19.1265 65.4417 22.6127 66.9358 26.3978C68.2307 29.386 68.9279 32.5734 69.2267 35.8604C69.4259 37.3545 70.5216 38.5498 72.0157 38.5498Z" fill="#0057FF"></path>
							</svg>
						</div>
					}
				</div>
			</div>
		);
	}
)